import { useContext } from 'react'
import { BuildingLibraryIcon, EnvelopeIcon, UserIcon } from '@heroicons/react/24/outline'
import type { Country as PhoneCountry } from 'react-phone-number-input'
import { Controller, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import {
	FormSection,
	FormSubsection,
	PayoutDetailsFields,
	PhoneNumberInput,
	Select
} from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useSelect } from 'shared/hooks'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import type { Country } from 'shared/types/general.types'

type UserFormSectionsProps = {
	form: UseFormReturn<FieldValues>
	countries: Country[] | undefined
	isCountriesLoading: boolean
}

export const UserFormSections = ({
	form,
	countries,
	isCountriesLoading
}: UserFormSectionsProps) => {
	const { t } = useTranslation()
	const settingsCtx = useContext(StoreSettingsContext)
	const isIceland = settingsCtx?.storeSettings?.country == 'IS'
	const {
		register,
		control,
		setValue,
		trigger,
		formState: { errors }
	} = form
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const fieldErrors = errors as any

	return (
		<div className="grid gap-5 lg:grid-cols-2">
			<FormSection
				title={t('Account')}
				description={t('Login and where payouts are sent.')}
				icon={<EnvelopeIcon />}>
				<InputGroup
					id="email"
					type="email"
					className="sm:col-span-2"
					label={t('Email')}
					placeholder="name@example.com"
					required
					{...register('email')}
					error={fieldErrors.email}
				/>
				{isIceland && (
					<InputGroup
						id="ssn"
						className="sm:col-span-2"
						label={t('SSN')}
						placeholder="0000000000"
						{...register('ssn')}
						error={fieldErrors.ssn}
					/>
				)}
				<FormSubsection
					title={t('Payout details')}
					description={t('Where payouts are sent.')}
					icon={<BuildingLibraryIcon />}
					tone="danger"
				/>
				<PayoutDetailsFields form={form} />
			</FormSection>

			<FormSection
				title={t('Personal information')}
				description={t('Name, contact and address.')}
				icon={<UserIcon />}
				tone="success">
				<InputGroup
					id="name"
					className="sm:col-span-2"
					label={t('Name')}
					placeholder={t('Full name')}
					{...register('name')}
					error={fieldErrors.name}
				/>
				<PhoneNumberInput
					id="phone"
					variant="form"
					className="sm:col-span-2"
					control={control}
					defaultCountry={settingsCtx?.storeSettings?.address?.country as PhoneCountry}
					label={t('Phone')}
					{...register('phone_number')}
					error={fieldErrors.phone_number}
				/>
				<Controller
					name="country"
					control={control}
					render={({ field }) => (
						<Select
							id="country"
							variant="form"
							className="sm:col-span-2"
							isClearable={false}
							isSearchable
							label={t('Country')}
							required
							options={countries?.map((option) => ({
								label: option.display_name,
								value: option.value
							}))}
							{...field}
							isLoading={isCountriesLoading}
							error={fieldErrors.country?.value}
							onChange={(data: any) =>
								handleSelectChange({ id: 'country', value: data?.value, label: data?.label })
							}
						/>
					)}
				/>
				<InputGroup
					id="address"
					className="sm:col-span-2"
					label={t('Address')}
					placeholder={t('Street and number')}
					required
					{...register('address.address')}
					error={fieldErrors.address?.address}
				/>
				<InputGroup
					id="city"
					label={t('City')}
					placeholder={t('City')}
					required
					{...register('address.city')}
					error={fieldErrors.address?.city}
				/>
				<InputGroup
					id="postal_code"
					label={t('Postal code')}
					placeholder="000"
					required
					{...register('address.postal_code')}
					error={fieldErrors.address?.postal_code}
				/>
			</FormSection>
		</div>
	)
}
